import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api, reconocimientoService } from '../../services/api';
import toast from 'react-hot-toast';
import { Modal } from '../../components/common';
import RichTextEditor from '../../components/common/RichTextEditor';
import AdjuntosInforme, { MAX_MB_TOTAL_OBSERVACION, superaTotalAdjuntos } from '../../components/common/AdjuntosInforme';

interface Adherente {
  id: string;
  partidoId: string;
  dni: string;
  nombre: string;
  apellido: string;
  domicilioCalle: string | null;
  domicilioNumero: string | null;
  localidadDeclarada: string | null;
  localidadPadron: string | null;
  estadoValidacion: EstadoValidacion;
  motivoEstado: string | null;
  aprobadoPor: string | null;
  aprobadoEn: string | null;
  motivoAprobacion: string | null;
  // SEGUNDA DIMENSIÓN (Art. 19): el aval firmado en la planilla de adherentes.
  // AL REVÉS que la ficha del afiliado: arranca en true. La supervisora no revisa aval
  // por aval —hace un paneo general— y solo destilda al que no cumple. Los destildados
  // no cuentan para el mínimo.
  avalAprobado: boolean;
  avalMotivo: string | null;
  avalRevisadoPor: string | null;
  avalRevisadoEn: string | null;
}

type EstadoValidacion = 'VALIDO' | 'NO_ENCONTRADO' | 'EXTRANJERO' | 'LOCALIDAD_NO_COINCIDE' | 'EXCLUIDO_LEGAL' | 'APROBADO_MANUAL';

interface ResumenAdherentes {
  total: number;
  validos: number;
  noEncontrados: number;
  localidadNoCoincide: number;
  excluidosLegales: number;
  extranjeros: number;
  aprobadosManuales: number;
  // Válidos contra el padrón, SIN descontar los avales destildados.
  validosPorPadron: number;
  // A cuántos se les destildó el aval.
  sinAvalAprobado: number;
  adherentesValidos: number;
  padronReferencia: number;
  minimoRequerido: number;
  alcanzaMinimo: boolean;
}

interface PartidoOption {
  id: string;
  denominacion: string;
  sigla: string | null;
}

const ESTADO_CONFIG: Record<EstadoValidacion, { label: string; color: string; bgColor: string; icon: string }> = {
  VALIDO:                { label: 'Válido',                color: 'text-green-700',  bgColor: 'bg-green-100',  icon: '✓' },
  NO_ENCONTRADO:         { label: 'No encontrado',         color: 'text-red-700',    bgColor: 'bg-red-100',    icon: '✗' },
  EXTRANJERO:            { label: 'Extranjero',            color: 'text-purple-700', bgColor: 'bg-purple-100', icon: '⊘' },
  LOCALIDAD_NO_COINCIDE: { label: 'Localidad no coincide', color: 'text-orange-700', bgColor: 'bg-orange-100', icon: '⚠' },
  EXCLUIDO_LEGAL:        { label: 'Excluido legal',        color: 'text-gray-700',   bgColor: 'bg-gray-100',   icon: '⊗' },
  APROBADO_MANUAL:       { label: 'Aprobado manual',       color: 'text-blue-700',   bgColor: 'bg-blue-100',   icon: '✎' },
};

export default function AdherentesPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [adherentes, setAdherentes] = useState<Adherente[]>([]);
  const [resumen, setResumen] = useState<ResumenAdherentes | null>(null);
  const [partidos, setPartidos] = useState<PartidoOption[]>([]);
  const [selectedPartidoId, setSelectedPartidoId] = useState<string>(() => searchParams.get('partido') ?? '');
  const [uploadPartidoId, setUploadPartidoId] = useState<string>('');

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingRespaldo, setUploadingRespaldo] = useState(false);
  const [respaldoFile, setRespaldoFile] = useState<File | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [inputSearch, setInputSearch] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedAdherente, setSelectedAdherente] = useState<Adherente | null>(null);
  const [approvalMotivo, setApprovalMotivo] = useState('');
  const [generandoObs, setGenerandoObs] = useState(false);
  const [enviandoObs, setIntimando] = useState(false);
  const [modalObservar, setModalReintimar] = useState<{ expId: string; texto: string; plazoDias: number } | null>(null);
  const [adjuntosObs, setAdjuntosObs] = useState<File[]>([]);
  const [obsTexto, setObsTexto] = useState<string | null>(null);
  const [cargandoObs, setCargandoObs] = useState(false);
  // id del adherente cuyo check de aval está en vuelo (para no permitir doble click).
  const [revisandoAval, setRevisandoAval] = useState<string | null>(null);
  const [revisandoAvalesMasivo, setRevisandoAvalesMasivo] = useState(false);
  const [obsCopiada, setObsCopiada] = useState(false);
  const autoObsRef = useRef(false);

  useEffect(() => {
    api.get('/partidos', { params: { pageSize: 200 } })
      .then(r => setPartidos(
        (r.data.data || [])
          .filter((p: any) => p.estado === 'EN_FORMACION' || p.estado === 'PENDIENTE_REVISION')
          .map((p: any) => ({ id: p.id, denominacion: p.denominacion, sigla: p.sigla ?? null }))
      ))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadAdherentes();
    loadResumen();
  }, [selectedPartidoId, currentPage, searchTerm, estadoFiltro]);

  useEffect(() => {
    const timer = setTimeout(() => { setSearchTerm(inputSearch); setCurrentPage(1); }, 500);
    return () => clearTimeout(timer);
  }, [inputSearch]);

  // ---- Revisión del aval (Art. 19) ----
  // Al destildar se pide motivo: un aval descartado sin explicación no sirve para la
  // observación al partido. Al cambiar el check se recarga el resumen (el mínimo se mueve).
  const toggleAval = async (adherente: Adherente, aprobado: boolean) => {
    let motivo: string | undefined;
    if (!aprobado) {
      const ingresado = window.prompt(
        `¿Por qué se descarta el aval de ${adherente.apellido}, ${adherente.nombre}?\n\n` +
        'Ej.: no figura en las planillas recibidas, falta la firma, los datos no coinciden.',
        adherente.avalMotivo || '',
      );
      if (ingresado === null) return;
      motivo = ingresado.trim() || undefined;
    }
    setRevisandoAval(adherente.id);
    try {
      await api.patch(`/adherentes/${adherente.id}/aval`, { aprobado, motivo });
      toast.success(aprobado ? 'Aval aprobado' : 'Aval descartado');
      loadAdherentes();
      loadResumen();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo registrar la revisión del aval');
    } finally {
      setRevisandoAval(null);
    }
  };

  // "Aprobar todos los avales": vuelve a aprobar los que estaban destildados.
  const toggleAvalesMasivo = async (aprobado: boolean) => {
    if (!selectedPartidoId) return;
    if (!window.confirm(
      '¿Aprobar los avales de TODOS los adherentes válidos de este partido?\n\n' +
      'Se van a volver a aprobar los que hoy están destildados. Queda registrado en el historial de cada uno.',
    )) return;
    setRevisandoAvalesMasivo(true);
    try {
      const res = await api.patch(`/adherentes/partido/${selectedPartidoId}/avales`, { aprobado });
      toast.success(`${res.data.actualizados} aval(es) actualizado(s)`);
      loadAdherentes();
      loadResumen();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudieron actualizar los avales');
    } finally {
      setRevisandoAvalesMasivo(false);
    }
  };

  const loadAdherentes = async () => {
    try {
      setLoading(true);
      const params: any = { page: currentPage, limit: 20 };
      if (searchTerm) params.buscar = searchTerm;
      if (estadoFiltro) params.estado = estadoFiltro;
      const url = selectedPartidoId ? `/adherentes/partido/${selectedPartidoId}` : `/adherentes`;
      const response = await api.get(url, { params });
      setAdherentes(response.data.data || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch { toast.error('Error al cargar adherentes'); }
    finally { setLoading(false); }
  };

  const loadResumen = async () => {
    if (!selectedPartidoId) { setResumen(null); return; }
    try {
      const response = await api.get(`/adherentes/partido/${selectedPartidoId}/resumen`);
      setResumen(response.data);
    } catch {}
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(e.target.files?.[0] ?? null);
    setUploadResult(null);
    e.target.value = '';
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !uploadPartidoId) {
      toast.error('Seleccione un partido y un archivo');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('archivo', selectedFile);
      const response = await api.post(`/adherentes/partido/${uploadPartidoId}/cargar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      setUploadResult(response.data);
      setSelectedFile(null);
      toast.success(`Procesado: ${response.data.cargadas} adherentes cargados`);
      if (selectedPartidoId === uploadPartidoId) { loadAdherentes(); loadResumen(); }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al cargar archivo');
    } finally { setUploading(false); }
  };

  const handleApprove = async () => {
    if (!selectedAdherente || !approvalMotivo.trim()) {
      toast.error('Debe ingresar un motivo de aprobación');
      return;
    }
    setUploadingRespaldo(true);
    try {
      let archivoRespaldo: string | null = null;
      if (respaldoFile) {
        const formData = new FormData();
        formData.append('archivo', respaldoFile);
        formData.append('tipo', 'OTRO');
        formData.append('titulo', `Respaldo aprobación manual - DNI ${selectedAdherente.dni}`);
        formData.append('partidoId', selectedAdherente.partidoId);
        const uploadRes = await api.post('/documentos', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        archivoRespaldo = uploadRes.data.data?.rutaArchivo || null;
      }
      await api.post(`/adherentes/${selectedAdherente.id}/aprobar`, {
        motivoAprobacion: approvalMotivo,
        archivoRespaldo,
      });
      toast.success('Adherente aprobado');
      setShowApprovalModal(false);
      setSelectedAdherente(null);
      setApprovalMotivo('');
      setRespaldoFile(null);
      loadAdherentes();
      loadResumen();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al aprobar');
    } finally {
      setUploadingRespaldo(false);
    }
  };

  const generarTextoObservacion = (result: any, nombrePartido: string, resumenFresh?: ResumenAdherentes | null): string => {
    const formatDni = (dni: string | null) =>
      dni ? dni.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.') : 'sin DNI';

    const p = (n: number, singular: string, plural: string) => n === 1 ? singular : plural;
    const hayNoEncontrados = (result.noEncontradosDetalle?.length ?? 0) > 0;
    const hayLocalidadNoCoincide = (result.localidadNoCoincideDetalle?.length ?? 0) > 0;
    const hayExcluidos = (result.excluidosDetalle?.length ?? 0) > 0;
    const hayExtranjeros = (result.extranjerosDetalle?.length ?? 0) > 0;
    const hayRechazados = (result.filasRechazadas?.length ?? 0) > 0;

    const lineasRechazadas = result.filasRechazadas
      ?.filter((f: any) => f.dni || f.apellido || f.nombre)
      .map((f: any) =>
        `- Fila ${f.fila} - DNI ${formatDni(f.dni)} (${f.apellido ?? '-'}, ${f.nombre ?? '-'}): ${f.motivos.join('; ').toLowerCase()}.`
      ).join('\n') ?? '';

    const totalIncorporados = result.totalProcesadas - result.rechazadas;
    const totalConObservaciones = result.conObservaciones ??
      ((result.noEncontradosDetalle?.length ?? 0) +
      (result.localidadNoCoincideDetalle?.length ?? 0) +
      (result.excluidosDetalle?.length ?? 0) +
      (result.extranjerosDetalle?.length ?? 0));

    let texto = `Se procesó el listado de adherentes presentado por el partido ${nombrePartido}, conforme a los Arts. 18 inc. a) y 19 de la Ley XII Nº 23.\n\nDel total de ${result.totalProcesadas} registros, ${totalIncorporados} fueron incorporados al registro de adherentes`;

    if (totalConObservaciones > 0 && hayRechazados) {
      texto += ` (${result.cargadas} ${p(result.cargadas, 'válido', 'válidos')} y ${totalConObservaciones} con observaciones), y ${result.rechazadas} no pudieron ser procesados por presentar datos obligatorios faltantes o inválidos:\n\n${lineasRechazadas}`;
    } else if (totalConObservaciones > 0) {
      texto += ` (${result.cargadas} ${p(result.cargadas, 'válido', 'válidos')} y ${totalConObservaciones} con observaciones).`;
    } else if (hayRechazados) {
      texto += `, y ${result.rechazadas} no pudieron ser procesados por presentar datos obligatorios faltantes o inválidos:\n\n${lineasRechazadas}`;
    } else {
      texto += '.';
    }

    const partes: string[] = [`${result.cargadas} ${p(result.cargadas, 'válido', 'válidos')}`];
    if ((result.noEncontradosDetalle?.length ?? 0) > 0) partes.push(`${result.noEncontradosDetalle.length} no ${p(result.noEncontradosDetalle.length, 'encontrado', 'encontrados')} en el padrón`);
    if ((result.extranjerosDetalle?.length ?? 0) > 0) partes.push(`${result.extranjerosDetalle.length} ${p(result.extranjerosDetalle.length, 'extranjero', 'extranjeros')}`);
    if ((result.excluidosDetalle?.length ?? 0) > 0) partes.push(`${result.excluidosDetalle.length} con inhabilitación legal`);
    if ((result.localidadNoCoincideDetalle?.length ?? 0) > 0) partes.push(`${result.localidadNoCoincideDetalle.length} con localidad no coincidente`);
    texto += `\n\nDetalle de la validación: ${partes.join(', ')}.`;

    if (hayNoEncontrados) {
      const lineas = result.noEncontradosDetalle.map((d: any) =>
        `- DNI ${formatDni(d.dni)} (${d.apellido ?? '-'}, ${d.nombre ?? '-'}): no figura en el padrón electoral.`
      ).join('\n');
      texto += `\n\nAsimismo, ${result.noEncontradosDetalle.length} ${p(result.noEncontradosDetalle.length, 'adherente no fue encontrado', 'adherentes no fueron encontrados')} en el padrón electoral:\n\n${lineas}`;
    }

    if (hayExtranjeros) {
      const lineas = result.extranjerosDetalle.map((d: any) =>
        `- DNI ${formatDni(d.dni)} (${d.apellido ?? '-'}, ${d.nombre ?? '-'}): figura en el padrón de extranjeros (Ley XII N° 21, Art. 7°).`
      ).join('\n');
      texto += `\n\nSe ${p(result.extranjerosDetalle.length, 'detectó', 'detectaron')} ${result.extranjerosDetalle.length} ${p(result.extranjerosDetalle.length, 'adherente extranjero', 'adherentes extranjeros')}:\n\n${lineas}`;
    }

    if (hayExcluidos) {
      const lineas = result.excluidosDetalle.map((d: any) =>
        `- DNI ${formatDni(d.dni)} (${d.apellido ?? '-'}, ${d.nombre ?? '-'}): ${d.motivo ?? 'inhabilitado legalmente'}.`
      ).join('\n');
      texto += `\n\nSe ${p(result.excluidosDetalle.length, 'detectó', 'detectaron')} ${result.excluidosDetalle.length} ${p(result.excluidosDetalle.length, 'adherente', 'adherentes')} con inhabilitaciones legales registradas:\n\n${lineas}`;
    }

    if (hayLocalidadNoCoincide) {
      const lineas = result.localidadNoCoincideDetalle.map((d: any) =>
        `- DNI ${formatDni(d.dni)} (${d.apellido ?? '-'}, ${d.nombre ?? '-'}): localidad en padrón: ${d.localidadPadron ?? 'desconocida'}.`
      ).join('\n');
      texto += `\n\nSe ${p(result.localidadNoCoincideDetalle.length, 'detectó', 'detectaron')} ${result.localidadNoCoincideDetalle.length} ${p(result.localidadNoCoincideDetalle.length, 'adherente', 'adherentes')} con localidad de domicilio en el padrón distinta a la del partido:\n\n${lineas}`;
    }

    // SEGUNDA DIMENSIÓN (Art. 19): los avales destildados tampoco cuentan para el
    // mínimo, así que la observación tiene que listarlos con el motivo que cargó la
    // revisora.
    const sinAval = result.sinAvalAprobadoDetalle ?? [];
    if (sinAval.length > 0) {
      const lineas = sinAval.map((d: any) =>
        `- DNI ${formatDni(d.dni)} (${d.apellido ?? '-'}, ${d.nombre ?? '-'})${d.motivo ? `: ${d.motivo}` : ''}.`
      ).join('\n');
      texto += `\n\nSe ${p(sinAval.length, 'detectó', 'detectaron')} ${sinAval.length} ${p(sinAval.length, 'adherente', 'adherentes')} cuyo aval en las planillas de adherentes (Art. 19, Ley XII N° 23) no pudo tenerse por válido. Hasta su regularización no ${p(sinAval.length, 'será computado', 'serán computados')} para el mínimo exigido:\n\n${lineas}`;
    }

    if (resumenFresh ?? resumen) {
      const r = resumenFresh ?? resumen!;
      if (r.alcanzaMinimo) {
        texto += `\n\nRespecto al requisito mínimo de adherentes (Art. 18 inc. a) de la Ley XII N° 23): el partido cuenta con ${r.adherentesValidos} adherentes válidos sobre un mínimo de ${r.minimoRequerido} (padrón de referencia: ${r.padronReferencia.toLocaleString()} electores). Cumple el requisito.`;
      } else {
        texto += `\n\nAsimismo, se verifica que el partido no alcanza el mínimo de adherentes válidos requerido por el Art. 18 inc. a) de la Ley XII N° 23: cuenta con ${r.adherentesValidos} adherentes válidos sobre un mínimo de ${r.minimoRequerido} (padrón de referencia de ${r.padronReferencia.toLocaleString()} electores).`;
      }
    }
    return texto;
  };

  // Genera el texto de la observación y lo deja en pantalla, listo para copiar
  // (sin navegar al expediente). Reusa el mismo armado que "Generar observación".
  const verTextoObservacion = async () => {
    if (!selectedPartidoId) return;
    const partido = partidos.find(p => p.id === selectedPartidoId);
    const nombrePartido = partido ? `${partido.denominacion}${partido.sigla ? ` (${partido.sigla})` : ''}` : 'Partido';
    setCargandoObs(true);
    try {
      const [detalleRes, resumenRes] = await Promise.all([
        api.get(`/adherentes/partido/${selectedPartidoId}/observacion-detalle`),
        api.get(`/adherentes/partido/${selectedPartidoId}/resumen`),
      ]);
      setResumen(resumenRes.data);
      setObsTexto(generarTextoObservacion(detalleRes.data, nombrePartido, resumenRes.data));
      setObsCopiada(false);
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Error al generar el texto de la observación');
    } finally { setCargandoObs(false); }
  };

  const copiarObservacion = async () => {
    if (!obsTexto) return;
    try {
      await navigator.clipboard.writeText(obsTexto);
      setObsCopiada(true);
      toast.success('Texto copiado al portapapeles');
      setTimeout(() => setObsCopiada(false), 2500);
    } catch { toast.error('No se pudo copiar. Seleccioná el texto manualmente.'); }
  };

  // Al llegar desde "Ver motivo" del documento (?obs=1), abrir el texto una vez
  // que el partido y su resumen están cargados.
  useEffect(() => {
    if (autoObsRef.current) return;
    if (searchParams.get('obs') === '1' && selectedPartidoId && partidos.length > 0) {
      autoObsRef.current = true;
      verTextoObservacion();
    }
  }, [selectedPartidoId, partidos]);

  const porcentajeCumplimiento = resumen
    ? Math.min(100, (resumen.adherentesValidos / resumen.minimoRequerido) * 100)
    : 0;

  return (
    <div className="space-y-6 p-6">

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full uppercase tracking-wide">
              Solo uso interno
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Adherentes</h1>
          <p className="text-gray-500">Art. 18 inc. a) + Art. 19 — Ley XII N° 23 — Constitución de partido</p>
        </div>
        <button
          onClick={() => { setUploadPartidoId(selectedPartidoId); setShowUploadModal(true); }}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          📤 Cargar Excel
        </button>
      </div>

      {/* Selector de partido */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Partido</label>
        <select
          value={selectedPartidoId}
          onChange={(e) => { setSelectedPartidoId(e.target.value); setCurrentPage(1); setResumen(null); }}
          className="w-full max-w-sm px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
        >
          <option value="">— Seleccionar partido —</option>
          {partidos.map(p => (
            <option key={p.id} value={p.id}>
              {p.sigla ? `${p.sigla} — ${p.denominacion}` : p.denominacion}
            </option>
          ))}
        </select>
      </div>

      {/* Resumen mínimo (25 o 4‰) */}
      {resumen && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Progreso hacia el mínimo de adherentes</h2>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${resumen.alcanzaMinimo ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
              {resumen.alcanzaMinimo ? '✓ Alcanza' : '⏳ Insuficiente'}
            </span>
          </div>
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span>{resumen.adherentesValidos.toLocaleString()} válidos</span>
              <span>Mínimo: {resumen.minimoRequerido.toLocaleString()}</span>
            </div>
            <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${resumen.alcanzaMinimo ? 'bg-green-500' : 'bg-purple-500'}`}
                style={{ width: `${porcentajeCumplimiento}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Padrón de referencia: {resumen.padronReferencia.toLocaleString()} electores</p>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-1.5">
            {[
              { label: 'En registro', value: resumen.total, cls: 'bg-gray-50' },
              { label: 'Válidos', value: resumen.validos, cls: 'bg-green-50' },
              { label: 'Aprobados', value: resumen.aprobadosManuales, cls: 'bg-blue-50' },
              { label: 'No encontrados', value: resumen.noEncontrados, cls: 'bg-red-50' },
              { label: 'Localidad ≠', value: resumen.localidadNoCoincide, cls: 'bg-orange-50' },
              { label: 'Excluidos', value: resumen.excluidosLegales, cls: 'bg-gray-50' },
              // El resumen ya devolvía `extranjeros` pero la grilla no lo mostraba.
              { label: 'Extranjeros', value: resumen.extranjeros, cls: 'bg-purple-50' },
              // SEGUNDA DIMENSIÓN (Art. 19): avales destildados. No cuentan al mínimo.
              { label: 'Aval rechazado', value: resumen.sinAvalAprobado, cls: 'bg-yellow-50' },
            ].map(({ label, value, cls }) => (
              <div key={label} className={`${cls} rounded-lg p-2 text-center`}>
                <div className="text-xl font-bold text-gray-700">{value}</div>
                <div className="text-xs text-gray-500">{label}</div>
              </div>
            ))}
          </div>

          {/* Revisión de los AVALES (Art. 19). A diferencia de las fichas de afiliación,
              acá los avales arrancan TODOS aprobados: la supervisora hace un paneo
              general de las planillas firmadas y solo destilda al que no cumple. */}
          <div className="mt-4 border-t pt-4 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm font-medium text-gray-800">Avales de las planillas de adherentes</p>
              <p className="text-xs text-gray-600 max-w-2xl">
                Los avales se reciben en papel. Arrancan todos aprobados: destildá el aval del
                adherente que no cumpla. <strong>Los destildados no cuentan para el mínimo.</strong>
              </p>
              {resumen.sinAvalAprobado > 0 && (
                <p className="text-xs font-medium text-yellow-700 mt-1">
                  {resumen.sinAvalAprobado} aval(es) destildado(s) de {resumen.validosPorPadron} válidos por padrón.
                </p>
              )}
            </div>
            <button
              onClick={() => toggleAvalesMasivo(true)}
              disabled={revisandoAvalesMasivo || resumen.sinAvalAprobado === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
            >
              {revisandoAvalesMasivo ? 'Actualizando…' : '✓ Aprobar todos los avales'}
            </button>
          </div>
        </div>
      )}

      {/* Botón generar observación — aparece cuando hay cualquier problema */}
      {selectedPartidoId && resumen &&
        (resumen.noEncontrados > 0 || resumen.localidadNoCoincide > 0 ||
          resumen.excluidosLegales > 0 || resumen.extranjeros > 0 ||
          resumen.sinAvalAprobado > 0 || !resumen.alcanzaMinimo) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-amber-800 flex-1 min-w-[200px]">Hay adherentes con observaciones. Podés ver/copiar el texto o generar la observación al partido.</p>
          <button
            onClick={verTextoObservacion}
            disabled={cargandoObs}
            className="px-4 py-2 bg-white border border-amber-400 text-amber-700 rounded-lg hover:bg-amber-100 text-sm disabled:opacity-50 whitespace-nowrap"
          >
            {cargandoObs ? 'Generando...' : 'Ver / copiar texto'}
          </button>
          <button
            onClick={async () => {
              const partido = partidos.find(p => p.id === selectedPartidoId);
              const nombrePartido = partido ? `${partido.denominacion}${partido.sigla ? ` (${partido.sigla})` : ''}` : 'Partido';
              setGenerandoObs(true);
              try {
                const [detalleRes, resumenRes] = await Promise.all([
                  api.get(`/adherentes/partido/${selectedPartidoId}/observacion-detalle`),
                  api.get(`/adherentes/partido/${selectedPartidoId}/resumen`),
                ]);
                setResumen(resumenRes.data);
                const texto = generarTextoObservacion(detalleRes.data, nombrePartido, resumenRes.data);
                const r = await api.get('/expedientes', { params: { partidoId: selectedPartidoId, tipo: 'RECONOCIMIENTO', pageSize: 1 } });
                const exps = r.data.data || [];
                if (exps.length === 0) { toast.error('No se encontró expediente de reconocimiento para este partido'); return; }
                const exp = exps[0];
                if (exp.estado === 'EN_ANALISIS') {
                  navigate(`/expedientes/${exp.id}?tab=obs`, { state: { contenidoPrecargado: texto } });
                } else if (['EN_ADMISIBILIDAD', 'INTIMADO', 'OBSERVADO'].includes(exp.estado)) {
                  setModalReintimar({ expId: exp.id, texto, plazoDias: 15 });
                } else {
                  toast.error(`El expediente está en estado ${exp.estado} — no admite observaciones`);
                }
              } catch (err: any) {
                toast.error(err.response?.data?.error?.message || 'Error al generar observación');
              } finally { setGenerandoObs(false); }
            }}
            disabled={generandoObs}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm disabled:opacity-50 whitespace-nowrap ml-4"
          >
            {generandoObs ? 'Consultando...' : 'Generar observación'}
          </button>
        </div>
      )}

      {/* Texto de la observación, listo para copiar (sin salir de /adherentes) */}
      {obsTexto && (
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-800">Texto de la observación</h3>
            <div className="flex gap-2">
              <button
                onClick={copiarObservacion}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                {obsCopiada ? '✓ Copiado' : 'Copiar'}
              </button>
              <button
                onClick={() => setObsTexto(null)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                Cerrar
              </button>
            </div>
          </div>
          <textarea
            readOnly
            value={obsTexto}
            rows={12}
            className="w-full text-sm font-mono border border-gray-200 rounded-lg p-3 bg-gray-50 whitespace-pre-wrap"
          />
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex gap-4 flex-wrap">
        <input
          type="text"
          placeholder="Buscar por DNI, nombre o apellido..."
          value={inputSearch}
          onChange={(e) => setInputSearch(e.target.value)}
          className="flex-1 min-w-[240px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
        />
        <select
          value={estadoFiltro}
          onChange={(e) => { setEstadoFiltro(e.target.value); setCurrentPage(1); }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
        >
          <option value="">Todos los estados</option>
          <option value="VALIDO">✓ Válidos</option>
          <option value="APROBADO_MANUAL">✎ Aprobados manual</option>
          <option value="NO_ENCONTRADO">✗ No encontrados</option>
          <option value="LOCALIDAD_NO_COINCIDE">⚠ Localidad no coincide</option>
          <option value="EXCLUIDO_LEGAL">⊗ Excluidos</option>
          <option value="EXTRANJERO">⊘ Extranjeros</option>
          {/* Pseudo-estado de la SEGUNDA dimensión (el aval del Art. 19): no vive en el
              enum, el backend lo resuelve contra `avalAprobado`. */}
          <option value="AVAL_RECHAZADO">❌ Aval rechazado</option>
        </select>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : adherentes.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-5xl mb-4">📋</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay adherentes cargados</h3>
            <p className="text-gray-500">Usá el botón <strong>Cargar Excel</strong> de arriba a la derecha para comenzar.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">DNI</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domicilio</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Localidad Padrón</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" title="Aval firmado en la planilla de adherentes (Art. 19). Destildado, el adherente no cuenta para el mínimo.">
                      Aval
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {adherentes.map((adherente) => {
                    const cfg = ESTADO_CONFIG[adherente.estadoValidacion];
                    const puedeAprobar = ['NO_ENCONTRADO', 'LOCALIDAD_NO_COINCIDE', 'EXCLUIDO_LEGAL', 'EXTRANJERO'].includes(adherente.estadoValidacion);
                    // El aval solo se revisa en los adherentes que cuentan para el mínimo.
                    const cuentaParaElMinimo = ['VALIDO', 'APROBADO_MANUAL'].includes(adherente.estadoValidacion);
                    return (
                      <tr key={adherente.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-mono">{adherente.dni}</td>
                        <td className="px-4 py-3 text-sm">{adherente.apellido}, {adherente.nombre}</td>
                        <td className="px-4 py-3 text-sm">
                          {adherente.domicilioCalle ? `${adherente.domicilioCalle} ${adherente.domicilioNumero ?? ''}`.trim() : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">{adherente.localidadPadron || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cfg.bgColor} ${cfg.color}`}>
                            {cfg.icon} {cfg.label}
                          </span>
                          {adherente.motivoEstado && (
                            <p className="text-xs text-gray-500 mt-1 truncate max-w-[200px]" title={adherente.motivoEstado}>
                              {adherente.motivoEstado}
                            </p>
                          )}
                        </td>
                        {/* Check del aval. Solo aplica a los que cuentan para el mínimo. */}
                        <td className="px-4 py-3 align-top">
                          {cuentaParaElMinimo ? (
                            <label className="inline-flex items-start gap-2 cursor-pointer" title={adherente.avalMotivo || 'Destildar si el aval no cumple.'}>
                              <input
                                type="checkbox"
                                className="mt-0.5 w-4 h-4"
                                checked={adherente.avalAprobado}
                                disabled={revisandoAval === adherente.id}
                                onChange={(e) => toggleAval(adherente, e.target.checked)}
                              />
                              <span className="text-xs">
                                {adherente.avalAprobado
                                  ? <span className="text-green-700">OK</span>
                                  : <span className="text-red-700">Rechazado</span>}
                                {adherente.avalMotivo && (
                                  <span className="block text-gray-500 max-w-[180px] line-clamp-2 break-words">
                                    {adherente.avalMotivo}
                                  </span>
                                )}
                              </span>
                            </label>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {puedeAprobar && (
                            <button
                              onClick={() => { setSelectedAdherente(adherente); setApprovalMotivo(''); setShowApprovalModal(true); }}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Aprobar
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t flex justify-between items-center">
              <span className="text-sm text-gray-600">Página {currentPage} de {totalPages}</span>
              <div className="flex gap-2">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 border rounded text-sm disabled:opacity-50">Anterior</button>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border rounded text-sm disabled:opacity-50">Siguiente</button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal Cargar Excel */}
      <Modal isOpen={showUploadModal} onClose={() => { setShowUploadModal(false); setUploadResult(null); setSelectedFile(null); }} title="📤 Cargar Excel de Adherentes">
        <div className="space-y-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
            ⚠ Este listado debe ser el revisado por la SEP (columnas: DNI, Apellido, Nombre, Domicilio - Calle, Domicilio - Número, Localidad).
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Partido *</label>
            <select
              value={uploadPartidoId}
              onChange={e => setUploadPartidoId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-purple-500"
            >
              <option value="">— Seleccionar partido —</option>
              {partidos.map(p => (
                <option key={p.id} value={p.id}>
                  {p.sigla ? `${p.sigla} — ${p.denominacion}` : p.denominacion}
                </option>
              ))}
            </select>
          </div>
          <div className={`border-2 border-dashed rounded-lg p-8 text-center ${selectedFile ? 'border-purple-400 bg-purple-50' : 'border-gray-300'}`}>
            <input type="file" accept=".xlsx,.xls" onChange={handleFileSelect} className="hidden" id="excel-upload-adherentes" disabled={uploading} />
            {selectedFile ? (
              <div>
                <div className="text-4xl mb-2">📄</div>
                <p className="text-sm font-medium text-gray-800">{selectedFile.name}</p>
                <label htmlFor="excel-upload-adherentes" className="mt-2 inline-block text-xs text-purple-600 hover:text-purple-800 cursor-pointer underline">Cambiar archivo</label>
              </div>
            ) : (
              <label htmlFor="excel-upload-adherentes" className="cursor-pointer">
                <div className="text-4xl mb-2">📂</div>
                <p className="text-gray-600">Click para seleccionar archivo Excel</p>
              </label>
            )}
          </div>

          {uploadResult && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <p className="font-medium">Resultado:</p>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>Total filas: <strong>{uploadResult.totalProcesadas}</strong></div>
                <div className="text-green-600">Cargadas: <strong>{uploadResult.cargadas}</strong></div>
                <div className="text-red-600">Rechazadas: <strong>{uploadResult.rechazadas}</strong></div>
              </div>
              {uploadResult.detalleCargadas && (
                <div className="mt-2 pt-2 border-t grid grid-cols-2 gap-1 text-xs text-gray-600">
                  <div>Válidos: <strong className="text-green-700">{uploadResult.detalleCargadas.valido}</strong></div>
                  <div>No encontrados: <strong className="text-red-700">{uploadResult.detalleCargadas.no_encontrado}</strong></div>
                  <div>Localidad ≠: <strong className="text-orange-700">{uploadResult.detalleCargadas.localidad_no_coincide}</strong></div>
                  <div>Excluidos: <strong className="text-gray-700">{uploadResult.detalleCargadas.excluido}</strong></div>
                  <div>Extranjeros: <strong className="text-purple-700">{uploadResult.detalleCargadas.extranjero}</strong></div>
                </div>
              )}
            </div>
          )}
          <div className="flex gap-3">
            <button onClick={() => { setShowUploadModal(false); setUploadResult(null); setSelectedFile(null); }} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cerrar</button>
            <button
              onClick={handleFileUpload}
              disabled={!selectedFile || uploading || !uploadPartidoId}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {uploading ? 'Procesando...' : 'Enviar'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Aprobar */}
      <Modal isOpen={showApprovalModal} onClose={() => setShowApprovalModal(false)} title="✎ Aprobar Adherente Manualmente">
        {selectedAdherente && (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              ⚠️ La aprobación manual permite contar este adherente para el mínimo a pesar de no pasar la validación automática.
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p><strong>DNI:</strong> {selectedAdherente.dni}</p>
              <p><strong>Nombre:</strong> {selectedAdherente.apellido}, {selectedAdherente.nombre}</p>
              <p><strong>Estado actual:</strong> {ESTADO_CONFIG[selectedAdherente.estadoValidacion].label}</p>
              {selectedAdherente.motivoEstado && <p><strong>Motivo:</strong> {selectedAdherente.motivoEstado}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Motivo de aprobación *</label>
              <textarea
                value={approvalMotivo}
                onChange={(e) => setApprovalMotivo(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="Ej: Documentación respaldatoria presentada en mesa de entradas..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Archivo de respaldo <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              <input
                type="file"
                onChange={(e) => setRespaldoFile(e.target.files?.[0] ?? null)}
                className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {respaldoFile && (
                <p className="text-xs text-gray-500 mt-1">📎 {respaldoFile.name}</p>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowApprovalModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
              <button onClick={handleApprove} disabled={!approvalMotivo.trim() || uploadingRespaldo} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {uploadingRespaldo ? 'Subiendo...' : 'Aprobar'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal: observación de adherentes (etapa análisis) */}
      {modalObservar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <h3 className="text-lg font-bold mb-2">Enviar observación al partido</h3>
            <p className="text-sm text-gray-600 mb-4">
              Revisá y editá el texto si es necesario. Se cursa como observación de la etapa de
              análisis (documentación solicitada), no como intimación de admisibilidad.
            </p>
            <RichTextEditor
              value={modalObservar.texto}
              onChange={(val) => setModalReintimar(prev => prev ? { ...prev, texto: val } : null)}
              minHeight={300}
            />
                        <div className="mt-4">
              <AdjuntosInforme
                archivos={adjuntosObs}
                onChange={setAdjuntosObs}
                titulo="Adjuntar documentos (opcional)"
                ayuda="Hasta 50 MB por archivo y 50 MB en total. El partido los descarga desde el expediente."
                maxMb={MAX_MB_TOTAL_OBSERVACION}
                avisoCorreo={false}
              />
            </div>
            <div className="flex items-center justify-end gap-3 mt-4">
              <label className="text-sm text-gray-600 mr-auto flex items-center gap-2">
                Plazo para responder
                <select
                  value={modalObservar.plazoDias}
                  onChange={(e) => setModalReintimar(prev => prev ? { ...prev, plazoDias: Number(e.target.value) } : null)}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  <option value={15}>15 días</option>
                  <option value={30}>30 días</option>
                  <option value={0}>Sin plazo</option>
                </select>
              </label>
              <button onClick={() => { setModalReintimar(null); setAdjuntosObs([]); }} disabled={enviandoObs} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">Cancelar</button>
              <button
              onClick={async () => {
                if (superaTotalAdjuntos(adjuntosObs)) {
                  toast.error(`Los adjuntos suman más de ${MAX_MB_TOTAL_OBSERVACION} MB. Mandá los más pesados en otra observación.`);
                  return;
                }
                setIntimando(true);
                try {
                    // Los adherentes son documentación del Art. 19: la etapa es
                    // ANÁLISIS, así que se cursa por el endpoint de reconocimiento
                    // (crea tipo OBSERVACION). Antes usaba /admisibilidad/:id/intimar,
                    // que la creaba como INTIMACION y quedaba fuera del contador de
                    // observaciones de la tab Análisis en /gestion.
                    const datos = new FormData();
                    datos.append('texto', modalObservar.texto);
                    if (modalObservar.plazoDias === 0) datos.append('sinPlazo', 'true');
                    else datos.append('plazoDias', String(modalObservar.plazoDias));
                    adjuntosObs.forEach((archivo) => datos.append('archivos', archivo));
                    await reconocimientoService.observar(modalObservar.expId, datos);
                    toast.success('Observación enviada al partido');
                    setModalReintimar(null);
                    setAdjuntosObs([]);
                    navigate(`/expedientes/${modalObservar.expId}?tab=obs`);
                  } catch (err: any) {
                    toast.error(err.response?.data?.error?.message || 'Error al enviar la observación');
                  } finally { setIntimando(false); }
                }}
                disabled={!modalObservar.texto.trim() || enviandoObs}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                {enviandoObs ? 'Enviando...' : 'Enviar Observación'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
